// Региональность покупателя. Один хук, смонтированный в SiteLayout:
//   1) первый вход гостя — определяем город по геолокации (DaData reverse через
//      /geo/reverse); при отказе/ошибке остаётся дефолт «Москва»;
//   2) вошедший покупатель — сервер источник города: подставляем его город из
//      профиля в префы, а изменения города шлём на сервер (buyer_profiles.city).
import { useEffect } from "react";

import { api } from "@/api";
import { usePreferences } from "@/store/preferences";
import { useProfile } from "@/store/profile";

// Сохранить выбранный/определённый город в профиль (только для вошедшего
// покупателя). Fire-and-forget: ошибка сети не должна ломать навигацию.
export function saveCityToServer(city: string, countryCode: string): void {
	if (!useProfile.getState().token) return;
	void api.buyer.updateProfileExt({ city, countryCode }).catch(() => undefined);
}

export function useRegionSync(): void {
	const cityChosen = usePreferences((s) => s.cityChosen);
	const geoPrompted = usePreferences((s) => s.geoPrompted);
	const prefsCity = usePreferences((s) => s.city);
	const token = useProfile((s) => s.token);
	const profileCity = useProfile((s) => s.profile?.city);
	const profileCountry = useProfile((s) => s.profile?.countryCode);

	// 1) Автоопределение города при первом входе (спрашиваем гео один раз).
	useEffect(() => {
		if (cityChosen || geoPrompted) return;
		if (typeof navigator === "undefined" || !navigator.geolocation) {
			usePreferences.getState().setGeoPrompted();
			return;
		}
		let cancelled = false;
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				// setGeoPrompted ставим в .finally (после setLocation): иначе смена
				// этой зависимости перезапустила бы эффект и cleanup отменил бы
				// применение определённого города.
				void api.geo
					.reverse(pos.coords.latitude, pos.coords.longitude)
					.then((r) => {
						if (cancelled || !r.city) return;
						const { countryCode, countryName } = usePreferences.getState();
						usePreferences.getState().setLocation({ code: countryCode, name: countryName }, r.city);
						saveCityToServer(r.city, countryCode);
					})
					.catch(() => undefined)
					.finally(() => {
						if (!cancelled) usePreferences.getState().setGeoPrompted();
					});
			},
			// Отказ/ошибка/таймаут — город остаётся дефолтным («Москва»).
			() => usePreferences.getState().setGeoPrompted(),
			{ timeout: 8000, maximumAge: 10 * 60 * 1000 },
		);
		return () => {
			cancelled = true;
		};
	}, [cityChosen, geoPrompted]);

	// 2) Вошедший покупатель: город из профиля — источник истины. Подставляем его
	//    в префы, если отличается (сервер синхронизирует web и приложение).
	useEffect(() => {
		if (!token || !profileCity || profileCity === prefsCity) return;
		const { countryCode, countryName } = usePreferences.getState();
		usePreferences
			.getState()
			.setLocation({ code: profileCountry ?? countryCode, name: countryName }, profileCity);
	}, [token, profileCity, profileCountry, prefsCity]);
}
